import { useEffect, useState } from "react";
import { X } from "lucide-react";
import Logo from "./Logo";
import { useMonetizationState } from "../lib/monetization";

/**
 * Welcome popup shown immediately after a successful registration.
 * - Auto-closes after 5 seconds (configurable via `autoCloseMs`)
 * - Can also be closed manually by clicking the cross or backdrop
 * - The body of the message is conditional on monetization state:
 *     • free mode (default)            → 12-month free banner
 *     • paid mode (admin enabled)      → standard welcome
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   ...after register success: setOpen(true);
 *   <WelcomePopup open={open} role="visiteur" onClose={() => setOpen(false)} />
 */
export default function WelcomePopup({ open, onClose, role = "visiteur", autoCloseMs = 5000 }) {
  const monet = useMonetizationState();
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (!open) return;
    setProgress(100);
    const start = Date.now();
    const tick = setInterval(() => {
      const left = Math.max(0, autoCloseMs - (Date.now() - start));
      setProgress((left / autoCloseMs) * 100);
      if (left <= 0) { clearInterval(tick); onClose && onClose(); }
    }, 80);
    return () => clearInterval(tick);
  }, [open, autoCloseMs, onClose]);

  if (!open) return null;

  // Determine if we're in free mode for this role
  const isFree = role === "etablissement" ? !monet.subscriptionsPaid : !monet.visitorsPaid;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="welcome-popup-backdrop"
    >
      <div
        className="relative w-full max-w-md bg-white border border-border shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="welcome-popup"
      >
        <button
          aria-label="Fermer"
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center text-subtle hover:text-ink hover:bg-surface"
          data-testid="welcome-popup-close-btn"
        >
          <X size={18} />
        </button>

        <div className="p-7 pb-6 flex flex-col items-center text-center">
          <Logo variant="symbol" height={56} className="mb-4" />
          <h2 className="font-display text-2xl font-black text-ink leading-tight">
            🎉 Bienvenue sur BypassGo !
          </h2>
          {isFree ? (
            <div className="mt-4 text-sm text-subtle leading-relaxed space-y-2" data-testid="welcome-popup-body-free">
              <p>Profitez d'un accès <strong className="text-ink">gratuit pendant 12 mois</strong> à l'ensemble des fonctionnalités.</p>
              <p>À l'issue de cette période, certaines fonctionnalités pourront évoluer vers une offre payante.</p>
              <p className="text-xs italic">Cette évolution ne sera <strong>pas automatique</strong> : vous serez informé(e) en amont de toute activation éventuelle.</p>
              {role === "etablissement" && (
                <p className="text-xs text-ink pt-1" data-testid="welcome-popup-email-identifier-note">
                  Votre <strong>identifiant de connexion est votre adresse email</strong>.
                </p>
              )}
              <p className="pt-1">Merci de votre confiance 🙏</p>
            </div>
          ) : (
            <div className="mt-4 text-sm text-subtle leading-relaxed" data-testid="welcome-popup-body-paid">
              <p>Nous sommes ravis de vous accueillir sur l'application.</p>
              {role === "etablissement" && (
                <p className="mt-2 text-xs text-ink" data-testid="welcome-popup-email-identifier-note-paid">
                  Votre <strong>identifiant de connexion est votre adresse email</strong>.
                </p>
              )}
              <p className="mt-2">Merci de votre confiance.</p>
            </div>
          )}
        </div>

        {/* Auto-close progress bar */}
        <div className="h-1 w-full bg-surface overflow-hidden">
          <div
            className="h-full transition-[width] duration-75 ease-linear"
            style={{ width: `${progress}%`, background: "linear-gradient(90deg, #002FA7 0%, #00C853 100%)" }}
          />
        </div>
      </div>
    </div>
  );
}
